import { useAuthenticator } from "@aws-amplify/ui-react";
import { useNavigate } from "react-router-dom";
import { authClient } from "../utils/amplifyClient";
import {
    getCompany as getCompanyQuery,
    listCompanyOwners as listCompanyOwnersQuery,
    listResourceLibraries as listResourceLibrariesQuery,
    listSingleUnits,
    usersByCompanyId,
} from "../graphql/queries";
import { CompanyOwner } from "../types/schema";
import { CreateCompanyOwnerInput } from "../graphql/API";
import { iCompanyOwner, tExtendedCompanyWithCounts } from "../types";
import { useEffect, useMemo, useState } from "react";
import * as Sentry from "@sentry/browser";
import { DEFAULT_SUPER_ADMIN_PERMISSIONS, NETWORK_DOMAIN } from "../utils/constants";
import {
    createCompanyOwner as createCompanyOwnerMutation,
    createResourceLibrary as createResourceLibraryMutation,
    deleteCompany,
    deleteCompanyOwner as deleteCompanyOwnerMutation,
    deleteResourceLibrary as deleteResourceLibraryMutation,
    updateCompany,
    updateCompanyOwner,
} from "../graphql/mutations";
import { isDateInFuture } from "../utils/helperFunctions";
import { useUserContext } from "../contexts/UserContext";

import { StatsCardsRow } from "./site-admin/StatsCardsRow";
import { FilterTabs } from "./site-admin/FilterTabs";
import { CompanyOwnersTable } from "./site-admin/CompanyOwnersTable";
import { AddCompanyModal } from "./site-admin/AddCompanyModal";
import { ModuleActivationModal } from "./site-admin/ModuleActivationModal";
import { SubscriptionModal } from "./site-admin/SubscriptionModal";
import { ExpirationModal } from "./site-admin/ExpirationModal";
import { DeleteModal } from "./site-admin/DeleteModal";

import "../css/SiteAdminContent.css";



export const SiteAdminContent = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [displayData, setDisplayData] = useState<iCompanyOwner[]>([]);
    const [companyOwnerData, setCompanyOwnerData] = useState<CompanyOwner[]>([]);
    const [companyDataByOwner, setCompanyDataByOwner] = useState<tExtendedCompanyWithCounts[]>([]);

    // Existing modal state
    const [currentCompanyId, setCurrentCompanyId] = useState<string | null>(null);
    const [deleteModalInfo, setDeleteModalInfo] = useState<iCompanyOwner | null>(null);
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [isEditExpirationModalOpen, setIsEditExpirationModalOpen] = useState(false);
    const [editExpirationModalInfo, setEditExpirationModalInfo] = useState<iCompanyOwner | null>(null);
    const [isEditSubscriptionTypeModalOpen, setIsEditSubscriptionTypeModalOpen] = useState(false);
    const [editSubscriptionTypeInfo, setEditSubscriptionTypeInfo] = useState<iCompanyOwner | null>(null);
    const [subscriptionType, setSubscriptionType] = useState("");
    const [expirationDate, setExpirationDate] = useState<string>("");
    const [isAddCompanyOwnerModalOpen, setIsAddCompanyOwnerModalOpen] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [resetCompanyOwnerList, setResetCompanyOwnerList] = useState<string[]>([]);

    // New state for filter tabs and module modal
    const [activeTab, setActiveTab] = useState("all");
    const [isModuleModalOpen, setIsModuleModalOpen] = useState(false);
    const [moduleModalCompany, setModuleModalCompany] = useState<tExtendedCompanyWithCounts | null>(null);

    const { companyOwner, businessAdmin, company: loggedInCompany, setCompany } = useUserContext();
    const { user } = useAuthenticator((context) => [context.user, context.route]);
    const navigate = useNavigate();

    // ─── Data Fetching (preserved) ───────────────────────
    const getCompanyOwnerData = async () => {
        if (!businessAdmin) return;
        setIsLoading(true);
        try {
            const companyOwnerResponse = await authClient.graphql({ query: listCompanyOwnersQuery });
            const allCompanyOwnersData = companyOwnerResponse?.data?.listCompanyOwners?.items;
            setCompanyOwnerData(allCompanyOwnersData as CompanyOwner[]);

            const companyPromises = allCompanyOwnersData
                .filter((owner) => owner.companyId)
                .map(async (owner) => {
                    try {
                        const [companyResult, usersResult, unitsResult] = await Promise.all([
                            authClient.graphql({ query: getCompanyQuery, variables: { id: owner.companyId! } }),
                            authClient.graphql({ query: usersByCompanyId, variables: { companyId: owner.companyId! } }),
                            authClient.graphql({ query: listSingleUnits }),
                        ]);
                        const company = companyResult.data.getCompany as unknown as tExtendedCompanyWithCounts;
                        if (company) {
                            company.userCount = usersResult.data.usersByCompanyId.items.length;
                            company.trainingEnrollmentCount = usersResult.data.usersByCompanyId.items.filter((u) => !!u.trainingModuleEnrollment).length;
                            company.skillTrackingCount = usersResult.data.usersByCompanyId.items.filter((u) => !!u.skillTrackingModuleEnrollment).length;
                            company.pmCount = unitsResult.data.listSingleUnits.items.filter((u) => !!u.pmModuleEnrollment).length;
                            company.tireMaintenanceCount = unitsResult.data.listSingleUnits.items.filter((u) => !!u.tiresModuleEnrollment).length;
                            company.costTrackingCount = unitsResult.data.listSingleUnits.items.filter((u) => !!u.costTrackingModuleEnrollment).length;
                            company.equipmentCount = unitsResult.data.listSingleUnits.items.filter((u) => (u as { category?: { companyId?: string | null } | null }).category?.companyId === owner.companyId).length;
                        }
                        return company;
                    } catch (error) {
                        console.error(`Error fetching data for owner ${owner.id}:`, error);
                        return null;
                    }
                });

            const companies = await Promise.all(companyPromises);
            setCompanyDataByOwner(companies.filter((c) => c !== null) as tExtendedCompanyWithCounts[]);
        } catch (error) {
            console.error("error :>> ", error);
            Sentry.captureException(error);
        } finally {
            setIsLoading(false);
        }
    };

    // ─── Handlers (preserved) ────────────────────────────
    const handleSubscriptionChange = async (value: string) => {
        try {
            if (editSubscriptionTypeInfo) {
                const companyOwnerInput = { id: editSubscriptionTypeInfo.id as string, subscriptionType: value };
                const result = await authClient.graphql({ query: updateCompanyOwner, variables: { input: companyOwnerInput } });
                setCompanyOwnerData((prev) => prev.map((o) => (o.id === companyOwnerInput.id ? { ...o, ...result.data.updateCompanyOwner } : o)));

                const currentCompanies = companyDataByOwner.filter((c) => c.companyOwnerId === editSubscriptionTypeInfo.id);
                const updatePromises = currentCompanies.map((c) =>
                    authClient.graphql({ query: updateCompany, variables: { input: { id: c.id, subscriptionType: value } } })
                );
                const results = await Promise.all(updatePromises);
                setCompanyDataByOwner((prev) =>
                    prev.map((c) => {
                        const updated = results.find((r) => r.data.updateCompany.id === c.id);
                        return updated ? { ...c, ...updated.data.updateCompany } : c;
                    })
                );
            }
        } catch (error) {
            console.error("Error updating subscription:", error);
        } finally {
            setIsEditSubscriptionTypeModalOpen(false);
        }
    };

    const handleExpirationDateChange = async (value: string) => {
        try {
            if (editExpirationModalInfo) {
                const formatDateLocal = (dateString: string) => {
                    const date = new Date(dateString);
                    const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
                    const day = date.getUTCDate().toString().padStart(2, "0");
                    const year = date.getUTCFullYear();
                    return `${month}/${day}/${year}`;
                };
                const formattedDate = formatDateLocal(value);
                const currentCompany = companyDataByOwner.find((c) => c.id === currentCompanyId);
                if (currentCompany) {
                    const result = await authClient.graphql({
                        query: updateCompany,
                        variables: { input: { id: currentCompany.id, subscriptionExpiration: formattedDate } },
                    });
                    setCompanyDataByOwner((prev) =>
                        prev.map((c) => (c.id === result.data.updateCompany.id ? { ...c, ...result.data.updateCompany } : c))
                    );
                }
            }
        } catch (error) {
            console.error("Error updating expiration:", error);
        } finally {
            setIsEditExpirationModalOpen(false);
        }
    };

    const handleDeleteCompanyOwner = async () => {
        if (!deleteModalInfo) return;
        try {
            await authClient.graphql({ query: deleteCompanyOwnerMutation, variables: { input: { id: deleteModalInfo.id as string } } });

            let resourceLibraryItem = null;
            try {
                const resourceLibrary = await authClient.graphql({ query: listResourceLibrariesQuery });
                resourceLibraryItem = resourceLibrary.data?.listResourceLibraries?.items?.find((lib) => lib?.companyOwnerId === deleteModalInfo.id);
            } catch (error) {
                console.error("Failed to fetch resource libraries:", error);
                Sentry.captureException(error);
            }
            if (resourceLibraryItem?.id) {
                try {
                    await authClient.graphql({ query: deleteResourceLibraryMutation, variables: { input: { id: resourceLibraryItem.id } } });
                } catch (error) {
                    console.error("Failed to delete resource library:", error);
                    Sentry.captureException(error);
                }
            }

            const currentCompanies = companyDataByOwner.filter((c) => c.companyOwnerId === deleteModalInfo.id);
            await Promise.all(currentCompanies.map((c) => authClient.graphql({ query: updateCompany, variables: { input: { id: c.id, subscriptionType: "paused" } } })));

            setDeleteModalInfo(null);
            getCompanyOwnerData();
        } catch (error) {
            console.error("error :>> ", error);
            Sentry.captureException(error);
        } finally {
            setIsConfirmed(false);
        }
    };

    const handleSingleCompanyDelete = async (companyId: string) => {
        if (!deleteModalInfo) return;
        try {
            await authClient.graphql({ query: deleteCompany, variables: { input: { id: companyId } } });
            setDeleteModalInfo(null);
            getCompanyOwnerData();
        } catch (error) {
            console.error("error :>> ", error);
            Sentry.captureException(error);
        } finally {
            setIsConfirmed(false);
        }
    };

    const handleAddCompanyOwner = async (data: { name: string; email: string; companyName: string; subscriptionType: string }) => {
        try {
            setIsCreating(true);
            const input: CreateCompanyOwnerInput = {
                email: data.email.toLowerCase(),
                name: data.name,
                role: "super-admin",
                subscriptionType: data.subscriptionType,
                siteAdmin: false,
                discrepancyNotification: true,
                discrepancyNotificationTypes: `{ "inop": true, "warn": true }`,
                permissions: JSON.stringify(DEFAULT_SUPER_ADMIN_PERMISSIONS.map((item) => item.value)),
                domain: NETWORK_DOMAIN,
            };
            const response = await authClient.graphql({ query: createCompanyOwnerMutation, variables: { input } });
            if (response.data.createCompanyOwner.id) {
                try {
                    await authClient.graphql({ query: createResourceLibraryMutation, variables: { input: { companyOwnerId: response.data.createCompanyOwner.id } } });
                } catch (error) {
                    console.error("Error creating Resource Library:", error);
                }
            }
            await getCompanyOwnerData();
        } catch (error) {
            console.log(error);
            Sentry.captureException(error);
        } finally {
            setIsCreating(false);
            setIsAddCompanyOwnerModalOpen(false);
        }
    };

    // ─── Resend Invite (preserved) ─────────────────────
    const resetCompanyOwnerHandler = async (owner: iCompanyOwner) => {
        if (!owner?.id || !owner?.email) return;
        try {
            await authClient.graphql({ query: deleteCompanyOwnerMutation, variables: { input: { id: owner.id } } });

            let resourceLibraryItem = null;
            try {
                const resourceLibrary = await authClient.graphql({ query: listResourceLibrariesQuery });
                resourceLibraryItem = resourceLibrary.data?.listResourceLibraries?.items?.find((lib) => lib?.companyOwnerId === owner.id);
            } catch (error) {
                console.error("Failed to fetch resource libraries:", error);
                Sentry.captureException(error);
            }
            if (resourceLibraryItem?.id) {
                try {
                    await authClient.graphql({ query: deleteResourceLibraryMutation, variables: { input: { id: resourceLibraryItem.id } } });
                } catch (error) {
                    console.error("Failed to delete resource library:", error);
                    Sentry.captureException(error);
                }
            }

            const response = await authClient.graphql({
                query: createCompanyOwnerMutation,
                variables: {
                    input: {
                        email: owner.email.toLowerCase(),
                        name: owner.name,
                        role: owner.role || "super-admin",
                        subscriptionType: owner.subscriptionType,
                        siteAdmin: false,
                        discrepancyNotification: true,
                        discrepancyNotificationTypes: `{ "inop": true, "warn": true }`,
                        permissions: owner.permissions || "[]",
                        domain: NETWORK_DOMAIN,
                    },
                },
            });
            const newId = response.data?.createCompanyOwner?.id;
            if (newId) {
                try {
                    await authClient.graphql({ query: createResourceLibraryMutation, variables: { input: { companyOwnerId: newId } } });
                } catch (error) {
                    console.error("Error creating resource library:", error);
                    Sentry.captureException(error);
                }
            }
        } catch (error) {
            console.error("Company owner reset failed:", error);
            Sentry.captureException(error);
        }
    };

    // ─── New: Module Toggle Handler ──────────────────────
    const handleModuleToggle = async (companyId: string, moduleField: string, value: boolean) => {
        try {
            const result = await authClient.graphql({
                query: updateCompany,
                variables: { input: { id: companyId, [moduleField]: value } },
            });
            setCompanyDataByOwner((prev) =>
                prev.map((c) => (c.id === companyId ? { ...c, ...result.data.updateCompany } : c))
            );
            // Update the modal company reference too
            setModuleModalCompany((prev) => (prev && prev.id === companyId ? { ...prev, ...result.data.updateCompany } : prev));
            // If the toggled company is the logged-in user's company, update UserContext
            // so the left nav reacts immediately without a page refresh
            if (loggedInCompany && loggedInCompany.id === companyId) {
                setCompany(result.data.updateCompany);
            }
        } catch (error) {
            console.error("Error toggling module:", error);
            Sentry.captureException(error);
        }
    };

    // ─── Effects (preserved) ─────────────────────────────
    useEffect(() => {
        if (user) getCompanyOwnerData();
    }, []);

    useEffect(() => {
        const combinedData: iCompanyOwner[] = companyOwnerData.map((owner) => ({
            ...owner,
            company: companyDataByOwner.filter((c) => c.id === owner.companyId),
        }));
        setDisplayData(combinedData);
    }, [companyDataByOwner, companyOwnerData]);

    // ─── Filter Logic ────────────────────────────────────
    const filteredData = useMemo(() => {
        switch (activeTab) {
            case "paid":
                return displayData.filter((o) => o.subscriptionType === "paid-full-access" || o.subscriptionType === "paid-limited-access");
            case "demo":
                return displayData.filter((o) => o.subscriptionType === "free-demo");
            case "expired":
                return displayData.filter((o) => o.company.some((c) => !isDateInFuture(c.subscriptionExpiration)));
            default:
                return displayData;
        }
    }, [displayData, activeTab]);

    const tabCounts = useMemo(() => ({
        all: displayData.length,
        paid: displayData.filter((o) => o.subscriptionType === "paid-full-access" || o.subscriptionType === "paid-limited-access").length,
        demo: displayData.filter((o) => o.subscriptionType === "free-demo").length,
        expired: displayData.filter((o) => o.company.some((c) => !isDateInFuture(c.subscriptionExpiration))).length,
    }), [displayData]);

    // ─── Render ──────────────────────────────────────────
    return (
        <div className="page-container sam-container">
            {/* Page Header */}
            <div className="sam-page-header">
                <div>
                    <h1 className="sam-page-title">Company Management</h1>
                    <p className="sam-page-subtitle">Manage all tenant accounts, subscriptions, and module access</p>
                </div>
                <button className="sam-btn-add" onClick={() => setIsAddCompanyOwnerModalOpen(true)}>
                    <svg viewBox="0 0 16 16" fill="currentColor"><path d="M8 1a1 1 0 011 1v5h5a1 1 0 110 2H9v5a1 1 0 11-2 0V9H2a1 1 0 010-2h5V2a1 1 0 011-1z" /></svg>
                    Add Company
                </button>
            </div>

            {/* Stats Cards */}
            <StatsCardsRow companyOwnerData={companyOwnerData} companyDataByOwner={companyDataByOwner} />

            {/* Table Section */}
            <div className="sam-table-section">
                <div className="sam-table-header">
                    <h2 className="sam-section-title">
                        Company Owners <span className="sam-section-count">{displayData.length}</span>
                    </h2>
                    <FilterTabs activeTab={activeTab} onTabChange={setActiveTab} counts={tabCounts} />
                </div>

                <CompanyOwnersTable
                    displayData={filteredData}
                    isLoading={isLoading}
                    currentOwnerId={companyOwner?.id}
                    resentOwnerIds={resetCompanyOwnerList}
                    onResendInvite={async (owner) => {
                        if (owner.id) {
                            setResetCompanyOwnerList((prev) => [...prev, owner.id!]);
                            await resetCompanyOwnerHandler(owner);
                        }
                    }}
                    onClickCompanyName={(companyId) => navigate(`/customer/${companyId}`)}
                    onClickSubscription={(owner) => {
                        setEditSubscriptionTypeInfo(owner);
                        setSubscriptionType(owner.subscriptionType);
                        setIsEditSubscriptionTypeModalOpen(true);
                    }}
                    onClickExpiration={(owner, company) => {
                        setEditExpirationModalInfo(owner);
                        setCurrentCompanyId(company.id);
                        const exp = company.subscriptionExpiration;
                        if (!exp) {
                            setExpirationDate(new Date().toISOString().split("T")[0]);
                        } else {
                            const formatted = new Date(exp).toISOString().split("T")[0];
                            setExpirationDate(formatted);
                        }
                        setIsEditExpirationModalOpen(true);
                    }}
                    onClickModules={(company) => {
                        setModuleModalCompany(company);
                        setIsModuleModalOpen(true);
                    }}
                    onClickEdit={(owner) => {
                        setEditSubscriptionTypeInfo(owner);
                        setSubscriptionType(owner.subscriptionType);
                        setIsEditSubscriptionTypeModalOpen(true);
                    }}
                    onClickDelete={(owner) => setDeleteModalInfo(owner)}
                />
            </div>

            {/* Modals */}
            <AddCompanyModal
                isOpen={isAddCompanyOwnerModalOpen}
                onClose={() => setIsAddCompanyOwnerModalOpen(false)}
                onSubmit={handleAddCompanyOwner}
                isCreating={isCreating}
            />
            <ModuleActivationModal
                isOpen={isModuleModalOpen}
                onClose={() => { setIsModuleModalOpen(false); setModuleModalCompany(null); }}
                company={moduleModalCompany}
                onToggleModule={handleModuleToggle}
            />
            <SubscriptionModal
                isOpen={isEditSubscriptionTypeModalOpen}
                onClose={() => { setEditSubscriptionTypeInfo(null); setIsEditSubscriptionTypeModalOpen(false); }}
                currentType={subscriptionType}
                onConfirm={handleSubscriptionChange}
            />
            <ExpirationModal
                isOpen={isEditExpirationModalOpen}
                onClose={() => { setEditExpirationModalInfo(null); setIsEditExpirationModalOpen(false); }}
                currentDate={expirationDate}
                onConfirm={handleExpirationDateChange}
            />
            <DeleteModal
                isOpen={!!deleteModalInfo}
                onClose={() => { setIsConfirmed(false); setDeleteModalInfo(null); }}
                ownerInfo={deleteModalInfo}
                onDeleteOwner={handleDeleteCompanyOwner}
                onDeleteCompany={handleSingleCompanyDelete}
                isConfirmed={isConfirmed}
                onConfirmChange={setIsConfirmed}
            />
        </div>
    );
};
